import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Folder,
  File,
  Image,
  Video,
  Music,
  ArrowLeft,
  Play,
  Square,
  Check
} from 'lucide-react';
import AdminModal from './AdminModal';
import VideoHoverPreview from './VideoHoverPreview';
import { API_BASE_URL } from '../utils/api';

const FILE_ICONS = {
  folder: Folder,
  image: Image,
  video: Video,
  audio: Music,
  other: File
};

const FILE_COLORS = {
  folder: '#a78bfa',
  image: '#34d399',
  video: '#f472b6',
  audio: '#fbbf24',
  other: '#64748b'
};

function formatSize(bytes) {
  if (!bytes || bytes === 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * A modal dialog for browsing Storage and selecting files.
 * Props:
 *   open        - boolean
 *   onClose     - () => void
 *   onSelect    - (item) => void — called when user selects a file
 *   fileFilter  - 'image' | 'video' | 'audio' — which file types to show
 *   teamId      - string
 *   getToken    - async () => string
 *   title       - optional modal title override
 */
export default function StorageBrowserModal({ open, onClose, onSelect, fileFilter, folderSelect, teamId, getToken, title }) {
  const [items, setItems] = useState([]);
  const [currentPath, setCurrentPath] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Audio playback state
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  const load = useCallback(async () => {
    if (!open || !teamId) return;
    try {
      setLoading(true);
      const token = await getToken();
      const res = await fetch(
        `${API_BASE_URL}/api/storage?path=${encodeURIComponent(currentPath)}&teamId=${encodeURIComponent(teamId)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json();
      // Show folders + files matching the filter (or folders only)
      const filtered = (data.items || []).filter((it) => {
        if (folderSelect) return it.type === 'folder';
        if (it.type === 'folder') return true;
        return it.fileCategory === fileFilter;
      });
      setItems(filtered);
      setSelectedItem(null);
    } catch (e) {
      console.error('StorageBrowserModal load error:', e);
    } finally {
      setLoading(false);
    }
  }, [open, currentPath, teamId, getToken, fileFilter, folderSelect]);

  useEffect(() => { load(); }, [load]);

  // Reset path when modal opens
  useEffect(() => {
    if (open) {
      setCurrentPath('');
      setSelectedItem(null);
      stopAudio();
    }
  }, [open]);

  const navigateTo = useCallback((path) => {
    setCurrentPath(path);
    setSelectedItem(null);
    stopAudio();
  }, []);

  const goUp = useCallback(() => {
    const parts = currentPath.split('/');
    parts.pop();
    navigateTo(parts.join('/'));
  }, [currentPath, navigateTo]);

  const pathParts = currentPath ? currentPath.split('/') : [];

  // Audio playback
  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    setPlayingId(null);
  }, []);

  const toggleAudio = useCallback((item) => {
    if (playingId === item.id) {
      stopAudio();
      return;
    }
    stopAudio();
    if (item.publicUrl) {
      const audio = new Audio(item.publicUrl);
      audio.addEventListener('ended', () => setPlayingId(null));
      audio.play().catch(() => {});
      audioRef.current = audio;
      setPlayingId(item.id);
    }
  }, [playingId, stopAudio]);

  // Cleanup audio on unmount / close
  useEffect(() => () => stopAudio(), [stopAudio]);

  const handleSelect = useCallback(() => {
    if (folderSelect) {
      if (onSelect) onSelect({ fullPath: currentPath, name: currentPath.split('/').pop() || 'Storage root' });
      onClose();
      return;
    }
    if (selectedItem && onSelect) {
      onSelect(selectedItem);
      onClose();
    }
  }, [selectedItem, onSelect, onClose, folderSelect, currentPath]);

  const modalTitle = title || (folderSelect ? 'Select Folder' : ({
    image: 'Select Image',
    video: 'Select Video',
    audio: 'Select Audio'
  }[fileFilter] || 'Select File'));

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title={modalTitle}
      maxWidth="sm"
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            disabled={folderSelect ? false : !selectedItem}
            onClick={handleSelect}
          >
            {folderSelect ? (currentPath ? `Select "${currentPath.split('/').pop()}"` : 'Select Root') : 'Select'}
          </Button>
        </>
      }
    >
      {/* Breadcrumb */}
      <Box display="flex" alignItems="center" gap={0.5} mb={1.5}>
        {currentPath && (
          <IconButton size="small" onClick={goUp} sx={{ color: 'text.secondary' }}>
            <ArrowLeft size={16} />
          </IconButton>
        )}
        <Typography
          variant="body2"
          sx={{ color: currentPath ? 'text.secondary' : 'text.primary', cursor: currentPath ? 'pointer' : 'default', fontWeight: 600 }}
          onClick={() => navigateTo('')}
        >
          Storage
        </Typography>
        {pathParts.map((part, idx) => {
          const isLast = idx === pathParts.length - 1;
          const targetPath = pathParts.slice(0, idx + 1).join('/');
          return (
            <React.Fragment key={targetPath}>
              <Typography variant="body2" sx={{ color: 'text.disabled' }}>/</Typography>
              <Typography
                variant="body2"
                sx={{
                  color: isLast ? 'text.primary' : 'text.secondary',
                  cursor: isLast ? 'default' : 'pointer',
                  fontWeight: 600,
                  '&:hover': isLast ? {} : { color: 'text.primary' }
                }}
                onClick={isLast ? undefined : () => navigateTo(targetPath)}
              >
                {part}
              </Typography>
            </React.Fragment>
          );
        })}
      </Box>

      {/* Content */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={24} />
        </Box>
      ) : items.length === 0 ? (
        <Box py={4} textAlign="center">
          <Typography variant="body2" color="text.secondary">
            {currentPath ? 'No matching files in this folder.' : 'Storage is empty.'}
          </Typography>
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" gap={0.5}>
          {items.map((item) => {
            const isFolder = item.type === 'folder';
            const isSelected = selectedItem?.id === item.id;
            const isAudio = item.fileCategory === 'audio';
            const isPlaying = playingId === item.id;
            const Icon = FILE_ICONS[item.fileCategory] || FILE_ICONS[item.type] || FILE_ICONS.other;
            const color = FILE_COLORS[item.fileCategory] || FILE_COLORS[item.type] || FILE_COLORS.other;
            const previewSrc = (item.fileCategory === 'image' || item.fileCategory === 'video')
              ? (item.previewUrls?.s256 || item.previewUrls?.s128 || null)
              : null;

            return (
              <Box
                key={item.id}
                onClick={() => {
                  if (isFolder) {
                    navigateTo(item.fullPath);
                  } else {
                    setSelectedItem(item);
                  }
                }}
                onDoubleClick={() => {
                  if (!isFolder && onSelect) {
                    onSelect(item);
                    onClose();
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  px: 1.25,
                  py: 0.75,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: isSelected ? 'primary.dark' : 'transparent',
                  bgcolor: isSelected ? 'rgba(139, 92, 246, 0.08)' : 'transparent',
                  '&:hover': {
                    bgcolor: isSelected ? 'rgba(139, 92, 246, 0.12)' : 'rgba(255,255,255,0.04)',
                    '& .audio-play-btn': { opacity: 1 }
                  }
                }}
              >
                {/* Thumbnail / Icon */}
                {previewSrc && item.fileCategory === 'video' ? (
                  <VideoHoverPreview
                    thumbnailSrc={previewSrc}
                    videoSrc={item.publicUrl}
                    sx={{
                      width: 48, height: 48, borderRadius: '6px',
                      flexShrink: 0, bgcolor: `${color}22`
                    }}
                  />
                ) : previewSrc ? (
                  <Box
                    component="img"
                    src={previewSrc}
                    sx={{
                      width: 48, height: 48, borderRadius: '6px',
                      objectFit: 'cover', flexShrink: 0,
                      bgcolor: `${color}22`
                    }}
                  />
                ) : (
                  <Box sx={{
                    position: 'relative',
                    width: 48, height: 48, borderRadius: '6px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, bgcolor: `${color}22`
                  }}>
                    <Icon size={18} strokeWidth={2} style={{ color }} />
                    {/* Audio play overlay */}
                    {isAudio && (
                      <IconButton
                        className="audio-play-btn"
                        size="small"
                        onClick={(e) => { e.stopPropagation(); toggleAudio(item); }}
                        sx={{
                          position: 'absolute', inset: 0,
                          opacity: isPlaying ? 1 : 0,
                          transition: 'opacity 0.15s',
                          bgcolor: 'rgba(0,0,0,0.5)',
                          borderRadius: '6px',
                          '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' }
                        }}
                      >
                        {isPlaying
                          ? <Square size={14} fill="#fbbf24" style={{ color: '#fbbf24' }} />
                          : <Play size={14} fill="#fbbf24" style={{ color: '#fbbf24' }} />
                        }
                      </IconButton>
                    )}
                  </Box>
                )}

                {/* Name & size */}
                <Box flex={1} minWidth={0}>
                  <Typography variant="body2" fontWeight={600} noWrap sx={{ color: 'text.primary' }}>
                    {item.name}
                  </Typography>
                  {!isFolder && (
                    <Typography variant="caption" color="text.secondary">
                      {formatSize(item.size)}
                    </Typography>
                  )}
                </Box>

                {/* Selection check */}
                {isSelected && (
                  <Check size={16} style={{ color: '#a78bfa', flexShrink: 0 }} />
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </AdminModal>
  );
}
